/**
 * Phase 1.7 — Asset Templates module index, v2.
 *
 * Group-by: asset_entity_type. The "system" templates ship with HollerHub
 * and are read-only; ranch templates are user-authored. We surface the
 * distinction with a badge rather than two separate sections so the
 * group-by stays clean (entity type drives the layout, scope is a tag).
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileBadge, Plus, Sparkles, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, EmptyState, FilterBar, KpiCard, SectionHeader, ViewToggle, type ViewMode } from '@/components/v2';
import { authFetch } from '@/lib/auth';
import { useUiPrefs } from '@/lib/layout-flag';
import { usePageHeader } from '@/components/v2/layout/Header';
import { AddEntityDialog } from '@/components/v2/dialogs/AddEntityDialog';
import type { AssetEntityType, AssetTemplate } from '@/components/TemplateSelector';

const ENTITY_ORDER: AssetEntityType[] = ['vehicle', 'animal', 'pasture', 'implement', 'tool', 'garden', 'crop'];
const ENTITY_LABEL: Record<AssetEntityType, string> = {
  vehicle: 'Vehicle',
  animal: 'Animal',
  pasture: 'Pasture',
  implement: 'Implement',
  tool: 'Tool',
  garden: 'Garden',
  crop: 'Crop',
};

const VIEW_KEY = 'templates';

export function TemplatesPageV2(): JSX.Element {
  const navigate = useNavigate();
  const { prefs, patch } = useUiPrefs();
  const view: ViewMode = prefs.view_modes?.[VIEW_KEY] ?? 'grid';
  const [items, setItems] = useState<AssetTemplate[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [entityFilter, setEntityFilter] = useState<'all' | AssetEntityType>('all');
  const [scopeFilter, setScopeFilter] = useState<'all' | 'ranch' | 'system'>('all');
  const [addOpen, setAddOpen] = useState<boolean>(false);
  const [refreshTick, setRefreshTick] = useState<number>(0);

  usePageHeader(
    'Asset templates',
    <Button
      size="sm"
      onClick={() => setAddOpen(true)}
      className="bg-burnt-orange text-white hover:bg-burnt-orange/90 dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90"
    >
      <Plus className="h-4 w-4" />
      New template
    </Button>,
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    async function load(): Promise<void> {
      try {
        const data = await authFetch<{ items: AssetTemplate[] }>('/api/templates');
        if (!cancelled) setItems(data.items ?? []);
      } catch (err) {
        if (!cancelled) setError((err as Error).message ?? 'Failed to load templates');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => { cancelled = true; };
  }, [refreshTick]);

  const filtered = useMemo<AssetTemplate[]>(() => {
    const q = search.trim().toLowerCase();
    return items.filter((t) => {
      if (q && !`${t.name} ${t.description ?? ''}`.toLowerCase().includes(q)) return false;
      if (entityFilter !== 'all' && t.asset_entity_type !== entityFilter) return false;
      if (scopeFilter === 'ranch' && t.is_system === 1) return false;
      if (scopeFilter === 'system' && t.is_system !== 1) return false;
      return true;
    });
  }, [items, search, entityFilter, scopeFilter]);

  const grouped = useMemo<Record<AssetEntityType, AssetTemplate[]>>(() => {
    const out = ENTITY_ORDER.reduce<Record<AssetEntityType, AssetTemplate[]>>(
      (acc, e) => ({ ...acc, [e]: [] }),
      {} as Record<AssetEntityType, AssetTemplate[]>,
    );
    for (const t of filtered) out[t.asset_entity_type].push(t);
    return out;
  }, [filtered]);

  const totals = useMemo(() => {
    const ranch = items.filter((t) => t.is_system !== 1).length;
    const system = items.filter((t) => t.is_system === 1).length;
    return { ranch, system };
  }, [items]);

  const setView = (next: ViewMode): void => {
    void patch({ view_modes: { ...(prefs.view_modes ?? {}), [VIEW_KEY]: next } });
  };

  const hasActiveFilters = Boolean(search) || entityFilter !== 'all' || scopeFilter !== 'all';
  const clear = (): void => { setSearch(''); setEntityFilter('all'); setScopeFilter('all'); };

  return (
    <div className="space-y-6">
      <AddEntityDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        title="New Template"
        endpoint="/api/templates"
        fields={[
          { name: 'name', label: 'Name', required: true, placeholder: 'Standard cattle setup' },
          {
            name: 'asset_entity_type',
            label: 'Applies to',
            type: 'select',
            required: true,
            defaultValue: 'animal',
            options: ENTITY_ORDER.map((e) => ({ value: e, label: ENTITY_LABEL[e] })),
          },
          { name: 'description', label: 'Description', type: 'textarea' },
        ]}
        transform={(v) => ({
          name: v.name,
          asset_entity_type: v.asset_entity_type,
          description: v.description || null,
        })}
        onAdded={() => setRefreshTick((t) => t + 1)}
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <KpiCard
          label="Templates"
          icon={FileBadge}
          primary={{ label: 'Ranch', value: totals.ranch }}
          secondary={{ label: 'System', value: totals.system }}
        />
        <KpiCard label="Filter" primary={{ label: 'Showing', value: filtered.length }} secondary={{ label: 'Total', value: items.length }} />
        <KpiCard
          label="Coverage"
          primary={{
            label: 'Asset types covered',
            value: ENTITY_ORDER.filter((e) => grouped[e].length > 0).length,
          }}
        />
      </div>

      <FilterBar
        search={{ value: search, onChange: setSearch, placeholder: 'Search templates' }}
        filters={[
          <FilterSelect
            key="entity"
            value={entityFilter}
            onChange={(v) => setEntityFilter(v as 'all' | AssetEntityType)}
            options={[
              { value: 'all', label: 'All asset types' },
              ...ENTITY_ORDER.map((e) => ({ value: e, label: ENTITY_LABEL[e] })),
            ]}
          />,
          <FilterSelect
            key="scope"
            value={scopeFilter}
            onChange={(v) => setScopeFilter(v as 'all' | 'ranch' | 'system')}
            options={[
              { value: 'all', label: 'All scopes' },
              { value: 'ranch', label: 'Ranch templates' },
              { value: 'system', label: 'System templates' },
            ]}
          />,
        ]}
        actions={<ViewToggle value={view} onChange={setView} />}
        onClear={clear}
        hasActiveFilters={hasActiveFilters}
      />

      {error ? <div className="rounded-lg bg-clay/10 p-4 text-sm text-clay dark:bg-clay-dark/20 dark:text-clay-dark">{error}</div> : null}

      {loading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-40 rounded-xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={FileBadge}
          title={hasActiveFilters ? 'No matches' : 'No templates yet'}
          description={hasActiveFilters ? 'Try clearing filters.' : 'Templates capture reusable defaults for an asset type.'}
          action={
            hasActiveFilters ? <Button onClick={clear} variant="outline">Clear filters</Button> : <Button onClick={() => setAddOpen(true)}><Plus className="h-4 w-4" />New template</Button>
          }
        />
      ) : (
        ENTITY_ORDER.filter((e) => grouped[e].length > 0).map((entity) => (
          <SectionHeader key={entity} title={`${ENTITY_LABEL[entity]} templates`} count={grouped[entity].length} collapsible>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {grouped[entity].map((t) => (
                <Card
                  key={t.id}
                  interactive
                  onClick={() => navigate(`/templates/${t.id}`)}
                  title={t.name}
                  subtitle={t.description ?? 'No description'}
                  badge={
                    t.is_system === 1 ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-clay/10 px-2 py-0.5 font-mono text-2xs uppercase tracking-wider text-clay dark:bg-clay-dark/20 dark:text-clay-dark">
                        <Sparkles className="h-3 w-3" />
                        System
                      </span>
                    ) : null
                  }
                  footer={
                    <div className="flex items-center justify-between text-xs">
                      <Link
                        to={`/templates/${t.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-burnt-orange hover:underline dark:text-burnt-orange-dark"
                      >
                        Edit
                      </Link>
                      <Trash2 className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                    </div>
                  }
                />
              ))}
            </div>
          </SectionHeader>
        ))
      )}
    </div>
  );
}

function FilterSelect({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }): JSX.Element {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 rounded-md border border-border bg-surface px-2 text-sm shadow-sm dark:bg-surface-dark dark:border-border"
    >
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}
